import React, { useState } from 'react';
import { BarChart2 } from 'lucide-react';
import { useCompetitors } from '../hooks/useCompetitors';
import { MAX_CITY, MAX_QUERY, checkRequest, isRunning, marketFacts, ownLine, reportStatus, yesNo } from '../utils/competitors';
import { ago } from '../utils/botQuestions';

const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';
const input = 'bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading w-full';

function Report({ report, onCancel }) {
  const st = reportStatus(report.status);
  const facts = marketFacts(report.market);
  const list = Array.isArray(report.competitors) ? report.competitors : [];
  return (
    <article className="border border-seryi-bg-light rounded-2xl p-4 space-y-3" aria-label={`Análisis de ${report.query} en ${report.city}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-seryi-text">
        <span><strong className="text-seryi-heading">{report.query} en {report.city}</strong> · {ago(report.created_at)}</span>
        <span className={st.tone}>{st.label}</span>
      </div>
      {report.status === 'error' && <p className="text-[11px] text-rose-300">{report.error}</p>}
      {report.status === 'pendiente' && (
        <div className="flex justify-end">
          <button type="button" onClick={() => onCancel(report.id)} className="border border-seryi-bg-light text-seryi-heading text-[11px] font-bold px-3 py-2 rounded-xl">Cancelar</button>
        </div>
      )}
      {report.status === 'listo' && (
        <>
          {facts.length > 0 && (
            <dl className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {facts.map((f) => (
                <div key={f.label} className="bg-seryi-bg-dark rounded-xl p-2 text-center">
                  <dd className="text-sm font-extrabold text-seryi-heading">{f.value}</dd>
                  <dt className="text-[10px] text-seryi-text">{f.label}</dt>
                </div>
              ))}
            </dl>
          )}
          <p className="text-[11px] text-seryi-text">{ownLine(report.market)}</p>
          {report.analysis ? (
            <p className="text-xs text-seryi-heading whitespace-pre-wrap bg-seryi-bg-dark rounded-xl p-3">{report.analysis}</p>
          ) : (
            <p className="text-[11px] text-amber-300">La IA no pudo redactar el resumen esta vez; abajo están los datos.</p>
          )}
          {list.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] text-left">
                <caption className="sr-only">Competidores más fuertes</caption>
                <thead className="text-seryi-text"><tr><th className="py-1 pr-2">Negocio</th><th className="pr-2">Calificación</th><th className="pr-2">Reseñas</th><th className="pr-2">Sitio web</th><th className="pr-2">Abre domingo</th></tr></thead>
                <tbody className="text-seryi-heading">
                  {list.slice(0, 10).map((c) => (
                    <tr key={c.maps_url || c.name} className="border-t border-seryi-bg-light">
                      <td className="py-1 pr-2">{c.maps_url ? <a href={c.maps_url} target="_blank" rel="noopener noreferrer" className="underline">{c.name}</a> : c.name}</td>
                      <td className="pr-2">{c.rating ?? '—'}</td><td className="pr-2">{c.reviews ?? 0}</td><td className="pr-2">{yesNo(c.website)}</td><td className="pr-2">{yesNo(c.open_sunday)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-seryi-text">Basado solo en datos públicos de Google Maps; no incluye precios ni redes sociales de la competencia.</p>
        </>
      )}
    </article>
  );
}

export default function CompetitorPage() {
  const { reports, loading, missing, busy, request, cancel } = useCompetitors();
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [error, setError] = useState('');

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261021a_competitor_reports.sql</code> en Supabase para usar el análisis de competencia.</div>;
  }

  const running = reports.some((r) => isRunning(r.status));
  const submit = async (e) => {
    e.preventDefault();
    const problem = checkRequest({ query, city });
    if (problem) { setError(problem); return; }
    setError('');
    if (await request({ query, city })) { setQuery(''); setCity(''); }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><BarChart2 size={20} /> Análisis de competencia</h2>
        <p className="text-xs text-seryi-text mt-1">Mira cómo está tu rubro en tu ciudad según Google Maps: quién tiene mejor calificación, quién abre los domingos y qué puedes hacer tú este mes. Puedes pedir uno cada 30 días.</p>
      </div>

      <form onSubmit={submit} className={card} aria-label="Pedir análisis">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="cp-query" className="block text-[11px] text-seryi-text mb-1">Tu rubro</label>
            <input id="cp-query" maxLength={MAX_QUERY} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ej: barberías" className={input} />
          </div>
          <div>
            <label htmlFor="cp-city" className="block text-[11px] text-seryi-text mb-1">Ciudad</label>
            <input id="cp-city" maxLength={MAX_CITY} value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ej: Medellín" className={input} />
          </div>
        </div>
        {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
        <div className="flex justify-end">
          <button type="submit" disabled={busy || running} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl disabled:opacity-50">{running ? 'Hay un análisis en curso' : busy ? 'Pidiendo…' : 'Analizar mi competencia'}</button>
        </div>
      </form>

      {loading && <p className="text-xs text-seryi-text">Cargando…</p>}
      {reports.length > 0 && (
        <section className={card} aria-label="Tus análisis">
          {reports.filter((r) => r.status !== 'cancelado').map((r) => <Report key={r.id} report={r} onCancel={cancel} />)}
        </section>
      )}
      {!loading && reports.length === 0 && <p className="text-xs text-seryi-text">Todavía no has pedido ningún análisis.</p>}
    </div>
  );
}
